import { ReactNode, useContext, useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { BUYER_UPLOAD_PAYMENT_PROOF, REQUEST_CANCEL_PAIR_BUY_SELL_ORDER } from '@/constants/graphql/mutations'
import { GET_BUY_ORDER_UPLOADS_PRESIGNED, GET_SELL_ORDER_CASHFLOW_PROOF_PRESIGNED } from '@/constants/graphql/queries'
import { AddImageInterface, BuyOrder, ImageAsset } from '@/types/common'
import { getRemainingTime } from '@/utils/formatter'
import { preparePickedImageUpload } from '@/utils/imageUpload'
import { launchMediaLibraryAsync } from '@/utils/mediaPicker'
import { generateRandomFileName, uploadImageToPresigned } from '@/utils/url'
import { useAutomaticCountdown } from '@/hooks/useCountdown'
import { Button, ModalFooter, ModalHeader, ModalTitle, useModal, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { UploadImage } from '@/components/icon'
import { ImageButton } from '@/components/ImageButton'
import { useImageViewer } from '@/components/ImageViewer'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import { ChildOrderDetailSection } from '@/components/orders/child/DetailSection'
import { ChildOrderPaymentSection } from '@/components/orders/child/PaymentSection'
import RemainingTimeCountdown from '@/components/orders/child/RemainingTimeCountdown'
import { NotificationsContext } from '@/components/providers/notifications'
import SectionHeader from '@/components/SectionHeader'
import UploadVideoButton from '@/components/UploadVideoButton'

type videoUriValues = {
  videoUri: string
}

const defaultCanceledVideoUri: videoUriValues = {
  videoUri: ''
}

export function ChildOrderWaitingForPaymentScreen({
  data: orderData,
  type,
  parentInternalKey,
  parentId,
  refetch,
  requestAppeal
}: {
  data: BuyOrder
  type: 'buy' | 'sell'
  parentInternalKey?: string
  parentId?: string
  refetch: () => void
  requestAppeal: () => void
}) {
  const { t } = useTranslation()
  const loading = useLoadingIndicator()
  const { bottom } = useSafeAreaInsets()
  const toast = useToast()
  const [images, setImages] = useState<ImageAsset[]>([])
  const isVirtual = orderData.buyer === '0'

  const { openModal, closeModal } = useModal()
  const { control, handleSubmit, reset } = useForm<videoUriValues>({ defaultValues: defaultCanceledVideoUri })
  const {
    field: { onChange: onChangeVideoUri }
  } = useController({ control, name: 'videoUri' })
  const { setSource, onDeleteCallback } = useImageViewer()
  const { refresh } = useContext(NotificationsContext)

  function setVideoSource(source: string) {
    openModal({
      children: <ModalContent videoSource={source} />,
      onClose: closeModal
    })
  }

  const [buyerUploadPaymentProof] = useMutation(BUYER_UPLOAD_PAYMENT_PROOF, {
    onCompleted: () => {
      loading.stop()
      refetch()
      refresh()
    },
    onError: (error) => {
      refetch()
      loading.stop()
      console.error('buyerUploadPaymentProof error:', error)
      toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
    }
  })

  const [requestCancelPairBuySellOrder] = useMutation(REQUEST_CANCEL_PAIR_BUY_SELL_ORDER, {
    onCompleted: () => {
      loading.stop()
      refetch()
      refresh()
    },
    onError: (error) => {
      refetch()
      loading.stop()
      console.log(error)
      toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
    }
  })

  const [getSellOrderCashflowProofPresigned] = useLazyQuery(GET_SELL_ORDER_CASHFLOW_PROOF_PRESIGNED, {
    onError: (error) => console.log(error)
  })

  const [getBuyOrderUploadsPresigned] = useLazyQuery(GET_BUY_ORDER_UPLOADS_PRESIGNED, {
    onError: (error) => {
      loading.stop()
      console.error(error)
    }
  })

  const addImage: AddImageInterface = (
    uri: string,
    _index: number,
    fileName?: string | null,
    mimeType?: string | null
  ) => {
    const extension = typeof fileName === 'string' ? fileName.split('.').at(-1) : 'jpg'
    const name = `${generateRandomFileName()}.${extension}`
    const asset = {
      uri,
      fileName: name,
      mimeType
    }

    const nextImages = [...images]
    if (!nextImages[0]) {
      nextImages[0] = asset
    } else if (!nextImages[1]) {
      nextImages[1] = asset
    } else {
      nextImages[2] = asset
    }
    setImages(nextImages)
  }

  const deleteImage = (deleteIndex: number) => {
    setImages([...images.filter((_, index) => index !== deleteIndex)])
  }

  async function pickImage() {
    const result = await launchMediaLibraryAsync({
      mediaTypes: ['images'],
      quality: 1,
      allowsMultipleSelection: false
    })
    // console.log('image result', result)
    if (!result.canceled) {
      const prepared = await preparePickedImageUpload(result.assets[0])
      if (!prepared.ok) {
        toast.show({
          variant: 'error',
          description: t(prepared.reason === 'type' ? 'orders:imageFormatInvalid' : 'orders:imageTooLarge')
        })
        return
      }
      addImage(prepared.asset.uri, 0, prepared.asset.fileName, prepared.asset.mimeType)
    }
  }

  const handleViewAddImage = (index: number) => {
    if (images[index]) {
      setSource(images[index].uri)
      onDeleteCallback.current = () => deleteImage(index)
    } else {
      pickImage()
    }
  }

  const handleSellerCancel = async (formData: videoUriValues) => {
    closeModal()
    loading.start()
    // console.log({ buyOrderId: orderData.id, sellOrderId: parentId, ...formData })
    let extension = 'mp4'
    const mimeType = formData.videoUri.split(';')[0].split(':')[1]
    switch (mimeType) {
      case 'video/x-msvideo': {
        extension = 'avi'
        break
      }
      case 'video/mp4': {
        extension = 'mp4'
        break
      }
      case 'video/quicktime': {
        extension = 'mov'
        break
      }
    }
    const inputFileName = `${generateRandomFileName()}.${extension}`
    const response: Record<string, any> = await getSellOrderCashflowProofPresigned({
      variables: {
        sellOrderId: parentId,
        buyOrderId: orderData.id,
        fileName: inputFileName
      }
    })
    const uploadUrl = response.data.getSellOrderCashflowProofPresigned

    // Find the image
    let fileName
    try {
      const urlObject = new URL(uploadUrl)
      const paths = urlObject.pathname.split('/')
      fileName = paths[paths.length - 1]
    } catch (error) {
      // TODO: Shouldn't come here, but just in case, should set some status to error
      console.error('Error parsing URL:', error)
      loading.stop()
      return null
    }

    // Upload
    try {
      await uploadImageToPresigned({ uri: formData.videoUri }, fileName, uploadUrl)
    } catch (e) {
      console.error('Error uploading image: ', e)
      loading.stop()
      throw e
    }

    // Call backend
    requestCancelPairBuySellOrder({
      variables: {
        buyOrderId: orderData.id,
        sellOrderId: parentId,
        cashflowProofFileName: inputFileName
      }
    })
  }

  const handleBuyerUploadPaymentProof = async () => {
    // console.log({ id: data.id, paymentProofFileName: images })
    loading.start()
    let fileNames
    try {
      const responses = await Promise.all(
        images.map(({ fileName }) => {
          return getBuyOrderUploadsPresigned({
            variables: {
              buyOrderId: orderData.id,
              fileName
            }
          })
        })
      )
      // Find images
      fileNames = responses.map((response) => {
        const uploadUrl = response.data.getBuyOrderUploadsPresigned
        const urlObject = new URL(uploadUrl)
        const paths = urlObject.pathname.split('/')
        return decodeURIComponent(paths[paths.length - 1])
      })
      // Upload images
      await Promise.all(
        fileNames.map((fileName, index) => {
          return uploadImageToPresigned(images[index], fileName, responses[index].data.getBuyOrderUploadsPresigned)
        })
      )
    } catch (e) {
      loading.stop()
      console.log(e)
      console.log('fail to upload images!')
      toast.show({ variant: 'error', description: t('auth:toast.error.timeoutError') })
      return
    }

    buyerUploadPaymentProof({
      variables: {
        id: orderData.id,
        paymentProofFileName: images.map((image) => image.fileName)
      }
    })
  }

  function openSellerCancelModal() {
    openModal({
      children: <ModalContent videoSource={''} />,
      onClose: closeModal
    })
  }

  function ModalContent({ videoSource }: { videoSource: string }) {
    return (
      <>
        <ModalHeader className={'p-4'}>
          <ModalTitle>{t('orders:cancelOrder')}</ModalTitle>
        </ModalHeader>
        <View className={'px-4 py-2'}>
          <Text className={'text-sm text-text-secondary'}>
            {t('orders:cancelOrderVideoInstruction')}
          </Text>
          <Text className={'text-sm text-text-secondary'}>
            {t('orders:cancelOrderVideoSteps')}
          </Text>
        </View>
        <View className={'p-4'}>
          <UploadVideoButton
            videoSource={videoSource}
            setVideoSource={setVideoSource}
            onChangeVideoUri={onChangeVideoUri}
          />
        </View>
        <ModalFooter>
          <Button
            className={'flex-1'}
            variant={'outline'}
            size={'md'}
            onPress={() => {
              closeModal()
              reset()
            }}
          >
            {t('orders:back')}
          </Button>
          <Button
            className={'flex-1'}
            size={'md'}
            disabled={videoSource === 'loading' || !videoSource}
            onPress={handleSubmit(handleSellerCancel)}
          >
            {t('orders:confirmCancel')}
          </Button>
        </ModalFooter>
      </>
    )
  }

  return (
    <View className={'flex-1 bg-background-secondary'}>
      <ScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom + 72 }}>
        <View className={'gap-2 border-b border-border-10 bg-background-primary px-4 pb-[11px] pt-2'}>
          <Text className={'text-2xl font-semibold text-text-primary'}>
            {t(type === 'buy' ? 'orders:paymentInProgress' : 'orders:buyerPaymentInProgress')}
          </Text>
          <View className={'flex-row items-baseline gap-2'}>
            <Text className={'text-sm text-text-primary'}>{t('orders:paymentTimeRemaining')}</Text>
            <RemainingTimeCountdown time={isVirtual ? orderData.createdAt : orderData.pairedAt} constraint={30} />
          </View>
          <Text className={'text-sm text-text-secondary'}>
            {t(type === 'buy' ? 'orders:buyerPaymentInstruction' : 'orders:waitingForBuyerPayment')}
          </Text>
        </View>
        <ChildOrderPaymentSection data={orderData} type={type} />
        {type === 'buy' && (
          <>
            <SectionHeader>{t('orders:uploadPaymentProof')}</SectionHeader>
            <View className={'gap-2 border-y border-border-10 bg-background-primary px-4 py-[15px]'}>
              <View className={'flex-row gap-2'}>
                {[0, 1, 2].map((index) => (
                  <ImageButton
                    key={index}
                    image={images[index]?.uri}
                    onPress={() => handleViewAddImage(index)}
                    blankElement={<UploadImage className={'size-6 text-text-quaternary'} />}
                  />
                ))}
              </View>
              <Text className={'text-sm text-text-secondary'}>
                {t('orders:paymentProofUploadHint')}
              </Text>
            </View>
          </>
        )}
        <ChildOrderDetailSection data={orderData} type={type} parentInternalKey={parentInternalKey} />
      </ScrollView>
      {type === 'buy' && (
        <BottomActionPanel>
          <Button
            size={'md'}
            intent={'primary'}
            variant={'solid'}
            className={'flex-1'}
            disabled={images.length === 0 || loading.isLoading}
            onPress={handleBuyerUploadPaymentProof}
          >
            {t('orders:paid')}
          </Button>
        </BottomActionPanel>
      )}
      {type === 'sell' && (
        <BottomActionPanel>
          <Button
            size={'md'}
            intent={'primary'}
            variant={'outline'}
            className={'flex-1'}
            onPress={requestAppeal}
            disabled={loading.isLoading}
          >
              {t('orders:startAppeal')}
          </Button>
          <SellerCancelOrderButtonWrapper time={orderData.pairedAt} constraint={30} isVirtual={isVirtual}>
            <Button
              size={'md'}
              intent={'primary'}
              variant={'outline'}
              className={'flex-1'}
              onPress={openSellerCancelModal}
              disabled={loading.isLoading}
            >
              {t('orders:cancelOrder')}
            </Button>
          </SellerCancelOrderButtonWrapper>
        </BottomActionPanel>
      )}
    </View>
  )
}

function SellerCancelOrderButtonWrapper({
  time,
  constraint,
  isVirtual,
  children
}: {
  time: string | null
  constraint: number
  isVirtual: boolean
  children: ReactNode
}) {
  const { countdown: remainingTime } = useAutomaticCountdown({ startTime: getRemainingTime(time, constraint) })

  if (remainingTime !== 0 || isVirtual) {
    return null
  }

  return children
}
